import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Flex, Text, Spinner, Drawer, DrawerOverlay, DrawerContent, DrawerCloseButton, DrawerHeader,
  DrawerBody, DrawerFooter, Button, Alert, AlertIcon, Link, useToast,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import {
  getRequest, updateRequest, errorMessage, documentLabel, formatDateTime, formatChangeValue,
  STATUS, CHANNEL_LABEL, FIELD_LABEL,
} from 'api/administrador/documentRequests';
import { StatusPill } from 'components/monitoring/ai/common';
import StudentPicker from './StudentPicker';
import ConstancyPanel from './ConstancyPanel';
import RequestFields, { valuesFromRequest, bodyFromValues } from './RequestFields';

function Section({ title, children }) {
  return (
    <Box>
      <Text fontSize="12px" fontWeight="600" letterSpacing=".06em" textTransform="uppercase" color="gray.500" mb={2.5}>{title}</Text>
      {children}
    </Box>
  );
}

const EVENT_LABEL = {
  creada: 'Creada',
  editada: 'Editada',
  estado: 'Cambio de estado',
  asignada: 'Responsable',
};

function EventItem({ e }) {
  const changes = Object.entries(e.changes || {});
  return (
    <Box py={2.5} borderTop="1px solid" borderColor="gray.100" _first={{ borderTop: 0 }}>
      <Flex justify="space-between" gap={3} wrap="wrap">
        <Text fontSize="13px" fontWeight="600">{EVENT_LABEL[e.action] || e.action}</Text>
        <Text fontSize="12px" color="gray.500">{formatDateTime(e.createdAt)} · {e.userName || '—'}</Text>
      </Flex>
      {changes.map(([field, c]) => (
        <Text key={field} fontSize="13px" color="gray.700">
          {FIELD_LABEL[field] || field}: {formatChangeValue(field, c?.from)} → <b>{formatChangeValue(field, c?.to)}</b>
        </Text>
      ))}
      {e.summary && <Text fontSize="13px" color="gray.600">{e.summary}</Text>}
    </Box>
  );
}

/** Detalle editable de una solicitud con su historial. */
export default function RequestDrawer({ requestId, isOpen, onClose, options, canEdit, onChanged }) {
  const toast = useToast();
  const [request, setRequest] = useState(null);
  const [values, setValues] = useState(null);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fill = useCallback((r) => {
    setRequest(r);
    setValues(valuesFromRequest(r));
    setStudent(r.student ? { id: r.student.id, name: r.student.name, identity: r.student.identity } : null);
  }, []);

  const load = useCallback(async () => {
    if (!requestId) return;
    setLoading(true);
    setError('');
    try {
      fill(await getRequest(requestId));
    } catch (e) {
      setError(errorMessage(e));
      setRequest(null);
    } finally {
      setLoading(false);
    }
  }, [requestId, fill]);

  useEffect(() => {
    if (isOpen) load();
    else { setRequest(null); setValues(null); }
  }, [isOpen, load]);

  const original = useMemo(() => (request ? JSON.stringify(valuesFromRequest(request)) : ''), [request]);
  const studentChanged = (student?.id || null) !== (request?.student?.id || null);
  const dirty = !!values && (JSON.stringify(values) !== original || studentChanged);

  const onChange = (name, value) => setValues((v) => ({ ...v, [name]: value }));

  const save = async () => {
    setSaving(true);
    setError('');
    try {
      const body = bodyFromValues(values);
      if (studentChanged) body.studentId = student?.id || null;
      fill(await updateRequest(request.id, body));
      toast({ status: 'success', title: 'Solicitud guardada', duration: 3000, isClosable: true });
      if (onChanged) onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const status = request ? STATUS[request.status] : null;

  return (
    <Drawer isOpen={isOpen} onClose={saving ? () => {} : onClose} size="lg" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton isDisabled={saving} />
        <DrawerHeader borderBottomWidth="1px" pr={12}>
          {request ? (
            <Box>
              <Flex align="center" gap={2} wrap="wrap">
                <Text fontFamily="mono" fontSize="15px" color="gray.500">{request.number}</Text>
                {status && <StatusPill label={status.label} tone={status.tone} />}
                {request.isTest && <StatusPill label="Prueba" tone="gray" />}
              </Flex>
              <Text fontSize="18px" fontWeight="700" mt={1}>{documentLabel(request)}</Text>
              <Text fontSize="13px" color="gray.500" fontWeight="400">
                {request.requesterName || 'Sin nombre'} · {CHANNEL_LABEL[request.channel] || request.channel}
                {request.origin === 'agente' ? ' · registrada por el agente' : request.createdBy ? ` · registrada por ${request.createdBy}` : ''}
              </Text>
            </Box>
          ) : <Text fontSize="18px">Solicitud</Text>}
        </DrawerHeader>

        <DrawerBody>
          {loading && !request && <Flex justify="center" py={10}><Spinner color="#574feb" /></Flex>}
          {error && (
            <Alert status="error" borderRadius="8px" mb={4} fontSize="sm">
              <AlertIcon />
              {error}
            </Alert>
          )}
          {request && values && (
            <Flex direction="column" gap={6} py={2}>
              <Section title="Estudiante">
                {canEdit ? (
                  <StudentPicker value={student} onChange={setStudent} inputId={`dr-${request.id}-student`} />
                ) : (
                  <Text fontSize="sm">{student ? `${student.name} · código ${student.id}` : 'No está ligada a un estudiante'}</Text>
                )}
                {request.student && !studentChanged && (
                  <Link
                    as={RouterLink} to={`/plataforma/administrador/ver-estudiante/${request.student.id}`}
                    fontSize="13px" color="#574feb" display="inline-block" mt={2}
                  >
                    Ver ficha del estudiante
                  </Link>
                )}
                {!request.student && (request.requesterDocument || request.requesterName) && (
                  <Text fontSize="13px" color="gray.600" mt={2}>
                    Registrada como {request.requesterName || 'sin nombre'}{request.requesterDocument ? ` · Doc. ${request.requesterDocument}` : ''}
                  </Text>
                )}
              </Section>

              {request.type === 'constancia' && (
                <Section title="Constancia automática">
                  {dirty && (
                    <Text fontSize="12.5px" color="orange.600" mb={2}>
                      Guarda los cambios de la solicitud para que la revisión de la constancia los tenga en cuenta.
                    </Text>
                  )}
                  <ConstancyPanel
                    request={request}
                    canEdit={canEdit}
                    onIssued={async () => { await load(); if (onChanged) onChanged(); }}
                  />
                </Section>
              )}

              <Section title="Solicitud">
                <RequestFields
                  values={values} onChange={onChange} options={options} idPrefix={`dr-${request.id}`} readOnly={!canEdit}
                />
              </Section>

              <Section title="Historial">
                {(request.events || []).length === 0
                  ? <Text fontSize="sm" color="gray.500">Sin movimientos.</Text>
                  : request.events.map((e) => <EventItem key={e.id} e={e} />)}
              </Section>
            </Flex>
          )}
        </DrawerBody>

        {canEdit && request && (
          <DrawerFooter borderTopWidth="1px" gap={2}>
            <Text fontSize="13px" color="gray.500" mr="auto">{dirty ? 'Tienes cambios sin guardar.' : 'Sin cambios.'}</Text>
            <Button variant="ghost" onClick={() => fill(request)} isDisabled={!dirty || saving}>Descartar</Button>
            <Button bg="#574feb" color="white" _hover={{ bg: '#4a42d6' }} onClick={save} isLoading={saving} isDisabled={!dirty}>
              Guardar cambios
            </Button>
          </DrawerFooter>
        )}
      </DrawerContent>
    </Drawer>
  );
}
